import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { castList, episodes } from '@/mocks/siteData';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CastDetail() {
  const { id } = useParams();
  const member = castList.find((c) => c.id === id);

  useEffect(() => {
    if (member) {
      document.title = `${member.name} - 善良的小姨子角色详情`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute(
          'content',
          `${member.name}在《善良的小姨子》中饰演${member.role}。查看角色介绍、演员简介及相关剧集。`
        );
      }
    }
  }, [member]);

  if (!member) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-user-unfollow-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-semibold text-foreground-950 text-xl mb-2">
          未找到该演员信息
        </h1>
        <p className="text-sm text-foreground-500 mb-6">该演员资料可能已调整，请返回演员列表重新选择。</p>
        <Link
          to="/category/cast"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回演员列表
        </Link>
      </div>
    );
  }

  const relatedEpisodes = episodes.filter((ep) => member.episodes.includes(ep.episode));

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10 animate-fade-up">
      <Breadcrumb items={[{ label: '演员列表', to: '/category/cast' }, { label: member.name }]} />

      <div className="mt-6 flex flex-col lg:flex-row gap-8 lg:gap-10">
        <div className="w-full lg:w-[300px] shrink-0">
          <div className="rounded-lg overflow-hidden border border-background-200 bg-background-100">
            <div className="w-full aspect-[3/4]">
              <img
                src={member.avatar}
                alt={`${member.name} 饰演 ${member.role}`}
                title={`${member.name} - 善良的小姨子角色详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <Link
            to="/category/cast"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            返回演员列表
          </Link>
        </div>

        <div className="flex-1 min-w-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-3">
            <i className="ri-user-star-line w-4 h-4 flex items-center justify-center"></i>
            演员资料
          </span>
          <h1 className="font-heading font-bold text-foreground-950 text-3xl md:text-4xl mb-2">
            {member.name}
          </h1>
          <p className="text-primary-700 font-medium text-base md:text-lg mb-6">
            饰 {member.role}
          </p>

          <div className="bg-background-50 border border-background-200 rounded-lg p-6 mb-6">
            <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-3 flex items-center gap-2">
              <i className="ri-book-2-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              角色介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">{member.roleDesc}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-3 flex items-center gap-2">
                <i className="ri-profile-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                演员简介
              </h2>
              <p className="text-sm text-foreground-700 leading-loose">{member.bio}</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-3 flex items-center gap-2">
                <i className="ri-film-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                其他参演
              </h2>
              <p className="text-sm text-foreground-700 leading-loose">{member.works}</p>
            </div>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-6">
            <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-4 flex items-center gap-2">
              <i className="ri-links-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              相关剧集（{relatedEpisodes.length} 集）
            </h2>
            <div className="flex flex-wrap gap-2.5">
              {relatedEpisodes.map((ep) => (
                <Link
                  key={ep.episode}
                  to={`/play/${ep.episode}`}
                  title={`第${ep.episode}集 ${ep.title}`}
                  className="px-3.5 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-accent-400 hover:text-accent-700 hover:bg-accent-50 transition-colors"
                >
                  第{ep.episode}集
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}